import type { CredentialCard } from '@/types';
import { SponsorLogos } from './SponsorLogos';

const ROLE_LABELS: Record<string, string> = {
  hacker: 'hacker',
  staff: 'staff',
  sponsor: 'sponsor',
  mentor: 'mentor',
  juez: 'juez',
  invitado: 'invitado',
  diseno: 'diseño',
};

// The name shrinks a size once it stops fitting, and how soon that happens depends on the
// role — hacker cards carry a longer role word above the name.
function nameSizeClass(type: string, fullName: string): string {
  const limit = type === 'hacker' ? 18 : type === 'mentor' ? 17 : 20;

  return fullName.length > limit ? 'text-3xl' : 'text-4xl';
}

export function Hack25CredentialCard({ card }: { card: CredentialCard }) {
  const isHacker = card.type === 'hacker';
  const textColor = isHacker ? 'text-white' : 'text-black';
  const bgColor = isHacker ? 'bg-black' : 'bg-white';
  const roleLabel = ROLE_LABELS[card.type];

  return (
    <div
      className={`${bgColor} flex flex-col items-center overflow-hidden p-6`}
      style={{
        width: '535px',
        height: '847px',
        borderRadius: '30px',
      }}
    >
      <div className="mb-6 flex w-full items-center justify-between">
        <p
          className={`font-medium font-mono text-3xl ${textColor} tracking-wider`}
        >
          21→23 <span className="lowercase">nov</span>
        </p>
        <h1
          className={`font-logo text-4xl ${textColor} lowercase tracking-tighter`}
        >
          <span className="font-light">platanus hack</span>{' '}
          <span className="font-medium">[25]</span>
        </h1>
      </div>

      <div className="flex w-full flex-1 flex-col">
        <h2
          className={`text-center font-medium font-title text-7xl ${textColor} lowercase tracking-wide`}
        >
          {roleLabel}
        </h2>

        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          {card.fullName && (
            <div className="w-full px-2 text-center">
              <p
                className={`font-medium font-title ${textColor} uppercase tracking-wider ${nameSizeClass(
                  card.type,
                  card.fullName,
                )}`}
              >
                {card.fullName}
              </p>
              {card.company && (
                <p
                  className={`mt-1 font-medium font-title text-2xl ${textColor}`}
                >
                  {card.company}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-col items-center gap-3">
            <div className="p-0">
              <img
                src={card.qrDataUrl}
                alt={`QR Code for ${card.fullName || roleLabel}`}
                width={180}
                height={180}
                className="h-[180px] w-[180px]"
                style={{ imageRendering: 'crisp-edges' }}
              />
            </div>

            <p
              className={`font-title text-3xl ${textColor}`}
              style={{ height: '42px' }}
            >
              {card.githubUsername && `@${card.githubUsername}`}
            </p>
          </div>
        </div>

        <div className="w-full px-2">
          <img
            src="/assets/logos/ph25.svg"
            alt="P/H(25)"
            width={600}
            height={120}
            className="h-auto w-full"
            style={isHacker ? { filter: 'invert(1) brightness(2)' } : undefined}
          />
        </div>
      </div>

      <div className="mt-6 w-full">
        <SponsorLogos variant={isHacker ? 'hacker' : 'staff'} />
      </div>
    </div>
  );
}
